import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, Eye, EyeOff, Home as HomeIcon, CalendarDays } from "lucide-react";

const EMPTY = {
  title: "", description: "", highlight: "", start_date: "", end_date: "", conditions: "",
  button_text: "Je profite de l'offre", button_link: "/gestion", is_active: true, show_on_home: true, priority: 0,
};

const today = () => new Date().toISOString().slice(0, 10);

const statusOf = (o) => {
  if (!o.is_active) return { label: "Désactivée", cls: "bg-gray-100 text-gray-600" };
  if (o.end_date && o.end_date < today()) return { label: "Expirée", cls: "bg-red-50 text-red-600" };
  if (o.start_date && o.start_date > today()) return { label: "Programmée", cls: "bg-blue-50 text-blue-700" };
  return { label: "En ligne", cls: "bg-green-50 text-green-700" };
};

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR") : "—");

export const AdminOffers = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/offers`, { headers });
      setOffers(r.data);
    } catch {
      toast.error("Impossible de charger les offres");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ ...EMPTY, ...o, start_date: o.start_date || "", end_date: o.end_date || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.start_date && form.end_date && form.end_date < form.start_date) return toast.error("La date de fin doit être postérieure à la date de début");
    setSaving(true);
    const payload = { ...form, start_date: form.start_date || null, end_date: form.end_date || null, priority: Number(form.priority) || 0 };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    try {
      if (editing) await axios.put(`${API}/admin/offers/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/admin/offers`, payload, { headers });
      toast.success(editing ? "Offre mise à jour" : "Offre créée");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (o, field) => {
    try {
      await axios.put(`${API}/admin/offers/${o.id}`, { [field]: !o[field] }, { headers });
      load();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer l'offre « ${o.title} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/offers/${o.id}`, { headers });
      toast.success("Offre supprimée");
      load();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-offers-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl">Offres promotionnelles</h1>
            <p className="text-gray-500 mt-1">Créez et pilotez vos opérations commerciales. Les offres « En ligne » avec « Accueil » activé s'affichent sur la page d'accueil.</p>
          </div>
          <Button onClick={openNew} className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="offer-add-button">
            <Plus size={18} className="mr-2" /> Nouvelle offre
          </Button>
        </div>

        {loading ? (
          <p className="text-gray-500">Chargement…</p>
        ) : offers.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center text-gray-500" data-testid="offers-empty">
            <Tag className="mx-auto mb-4 text-[#D4AF37]" size={32} /> Aucune offre pour le moment.
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="offers-list">
            {offers.map((o) => {
              const st = statusOf(o);
              return (
                <Card key={o.id} className="border-0 shadow-sm" data-testid={`offer-row-${o.id}`}>
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge className={`${st.cls} border-0`} data-testid={`offer-status-${o.id}`}>{st.label}</Badge>
                          {o.show_on_home && <Badge variant="outline" className="border-[#D4AF37] text-[#B8952B]"><HomeIcon size={12} className="mr-1" /> Accueil</Badge>}
                          {o.priority > 0 && <Badge variant="outline">Priorité {o.priority}</Badge>}
                        </div>
                        <h3 className="font-serif text-xl leading-tight truncate" data-testid={`offer-title-${o.id}`}>{o.title}</h3>
                        {o.highlight && <p className="text-[#B8952B] font-semibold mt-1">{o.highlight}</p>}
                        {o.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{o.description}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(o)} title="Modifier" data-testid={`offer-edit-${o.id}`}><Pencil size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(o)} title="Supprimer" className="text-red-500 hover:text-red-700" data-testid={`offer-delete-${o.id}`}><Trash2 size={16} /></Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500 border-t pt-4">
                      <span className="flex items-center gap-2"><CalendarDays size={14} /> {fmtDate(o.start_date)} → {fmtDate(o.end_date)}</span>
                      <div className="flex items-center gap-5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch checked={o.is_active} onCheckedChange={() => toggle(o, "is_active")} data-testid={`offer-toggle-active-${o.id}`} />
                          <span>{o.is_active ? <Eye size={14} className="inline" /> : <EyeOff size={14} className="inline" />} Active</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch checked={o.show_on_home} onCheckedChange={() => toggle(o, "show_on_home")} data-testid={`offer-toggle-home-${o.id}`} />
                          <span>Accueil</span>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="o-title">Titre de l'offre *</Label>
              <Input id="o-title" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="OFFRE DE LANCEMENT – MAINGUY IMMOBILIER" data-testid="offer-form-title" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-desc">Description / texte promotionnel</Label>
              <Textarea id="o-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Bénéficiez de 10 % de réduction pendant 1 an…" data-testid="offer-form-description" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-high">Taux, montant ou avantage mis en avant</Label>
              <Input id="o-high" value={form.highlight} onChange={(e) => set("highlight", e.target.value)} placeholder="6,3 % TTC au lieu de 7 % TTC" data-testid="offer-form-highlight" />
              <p className="text-xs text-gray-500">Astuce : écrivez « X au lieu de Y » pour que X soit affiché en grand et Y barré.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="o-start">Début de validité</Label>
                <Input id="o-start" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="offer-form-start" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-end">Fin de validité</Label>
                <Input id="o-end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="offer-form-end" />
                <p className="text-xs text-gray-500">L'offre disparaît automatiquement du site après cette date.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-cond">Conditions et modalités</Label>
              <Textarea id="o-cond" rows={2} value={form.conditions} onChange={(e) => set("conditions", e.target.value)} placeholder="Pendant les 12 premiers mois de gestion locative…" data-testid="offer-form-conditions" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="o-btn">Texte du bouton</Label>
                <Input id="o-btn" value={form.button_text} onChange={(e) => set("button_text", e.target.value)} data-testid="offer-form-button-text" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-link">Lien du bouton</Label>
                <Input id="o-link" value={form.button_link} onChange={(e) => set("button_link", e.target.value)} placeholder="/gestion, /contact ou https://…" data-testid="offer-form-button-link" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="o-prio">Ordre de mise en avant</Label>
                <Input id="o-prio" type="number" min={0} value={form.priority} onChange={(e) => set("priority", e.target.value)} data-testid="offer-form-priority" />
                <p className="text-xs text-gray-500">Plus le nombre est élevé, plus l'offre est affichée en premier.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer pb-7">
                <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="offer-form-active" />
                <span>Offre active</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer pb-7">
                <Switch checked={form.show_on_home} onCheckedChange={(v) => set("show_on_home", v)} data-testid="offer-form-home" />
                <span>Afficher sur l'accueil</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="offer-form-submit">
                {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer l'offre"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOffers;
